import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Router } from '@angular/router';
import { User } from '../models/auth';
import { environment } from 'public/environments/environment';
import CryptoJS from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private networkStatus = new Subject<boolean>();

  menuLoaded = new BehaviorSubject<any>(false);
  generalRefreshDetails = new BehaviorSubject<boolean>(false);

  actionsPermissions = new BehaviorSubject<any>(null);
  updateActionsPermissions = new Subject();

  public currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

  public authUserData: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();
  // CryptoJS
  appEncryptToken = 'scotAngularAppTS';
  encKey = CryptoJS.enc.Utf8.parse(this.appEncryptToken);
  encIv = CryptoJS.enc.Utf8.parse(this.appEncryptToken);

  SignalRConnectionId = new BehaviorSubject<any>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    const authLS = localStorage.getItem('authUser');
    const ls = authLS ? JSON.parse(authLS) as User : '';
    if (ls) {
      ls.UserRoles = this.decryptPermissions(ls.UserRoles);
      ls.TrainingPermissions = this.decryptPermissions(ls.TrainingPermissions || []);
      this.currentUserSubject.next(ls);
    }
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }
  public get currentActionsPermissions(): any {
    return this.actionsPermissions.value;
  }

  encryptPermissions(permissions: string[]) {
    const encryptedPermissions: string[] = [];
    permissions.forEach(element => {
      encryptedPermissions.push(this.encrypt(element));
    });
    return encryptedPermissions;
  }

  decryptPermissions(permissions: string[]) {
    const decriptedPermissions: string[] = [];
    if (permissions.length) {
      permissions.forEach(element => {
        if (element) {
          decriptedPermissions.push(this.isEncrypted(element) ? this.decrypt(element) : element);
        }
      });
    }
    return decriptedPermissions;
  }

  encrypt(request: string) {
    return CryptoJS.AES.encrypt(
      request, this.encKey, {
      keySize: 16,
      iv: this.encIv,
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    }).toString();
  }

  decrypt(data: string) {
    return CryptoJS.AES.decrypt(
      data, this.encKey, {
      keySize: 16,
      iv: this.encIv,
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    }).toString(CryptoJS.enc.Utf8);
  }

  isEncrypted(data: string): boolean {
    try {
      // Attempt to decrypt the data
      const decrypted = this.decrypt(data);
      // If decryption results in a non-empty string, it's likely encrypted
      return decrypted.trim() !== '';
    } catch (error) {
      // If decryption fails, the data is not encrypted
      return false;
    }
  }

  authGoogle(data: any): any {
    return this.http.post<User>(`${environment.apiUrl}/Authenticate/AuthGoogleWeb`, data)
  }

  authApple(data: any): any {
    return this.http.post<User>(`${environment.apiUrl}/Authenticate/AuthApple'`, data)
  }

  login(data: any): any {
    return this.http.post<User>(
      `${environment.apiUrl}/Authenticate/login`, data)
      .pipe(map((user: User) => {
        this.currentUserSubject.next(user);
        this.saveUserSession(user);
      }));
  }

  registerConsumer(data: any): any {
    return this.http.post<User>(
      `${environment.apiUrl}/Consumer/RegisterConsumer`, data)
      .pipe(map((user: User) => {
        this.currentUserSubject.next(user);
        this.saveUserSession(user, true);
      }));
  }

  checkConsumerUserAndSentOTP(data: any): any {
    return this.http.post(`${environment.apiUrl}/Consumer/CheckConsumerUserAndSentOTP`, data);
  }

  consumerLogin(data: any): any {
    return this.http.post<User>(
      `${environment.apiUrl}/Consumer/ConsumerLogin`, data)
      .pipe(map((user: User) => {
        this.currentUserSubject.next(user);
        this.saveUserSession(user, true);
      }));
  }

  saveUserSession(user: User, isConsumer = false) {
    const curenrtUserValue = { ...user };
    curenrtUserValue.UserRoles = this.encryptPermissions(curenrtUserValue?.UserRoles || []);

    this.currentUserSubject.next(user);

    localStorage.setItem('authUser', JSON.stringify(curenrtUserValue));
    this.handleLoginRedirct(isConsumer);
  }

  handleLoginRedirct(isConsumer = false) {
    if (!isConsumer) {
      let baseRoute = '/admin';
      this.router.navigate([baseRoute]);
    }
  }

  logout() {
    this.clearUserData();
    this.router.navigate(['/login']);
  }

  clearUserData() {
    localStorage.removeItem('authUser');
    this.currentUserSubject.next(null);
  }

  // Verification APIs
  ChangePassword(password: string, userID: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/ChangePassword`, { password, userID, token });
  }
  verifyToken(userId: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/VerifyToken`, { userId, token });
  }

  // Reset Passwords APIs
  generateResetPasswordToken(email: string) {
    return this.http.get(`${environment.apiUrl}/Authenticate/GenerateResetPasswordToken?email=${email}`);
  }
  verifyResetToken(userId: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/VerifyResetToken`, { userId, token });
  }
  resetPassword(password: string, userID: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/ResetPassword`, { password, userID, token });
  }


  GetUserDetails() {
    const user = this.currentUserValue || new User();
    this.http.get(`${environment.apiUrl}/Account/UserProfile`).subscribe((data: any) => {
      localStorage.setItem('authUser', JSON.stringify(user));
      setTimeout(() => {
        location.reload();
      }, 100);
    }, error => {
      this.logout();
    });
  }

  RefeshUserToken(forceReload = false) {
    return this.http.post<any>(
      `${environment.apiUrl}/Authenticate/refresh-token`, {})
      .pipe(map(returnUser => {
        if (returnUser) {
          const user = { ...returnUser };
          user.Token = returnUser.Token;
          this.currentUserSubject.next(returnUser);
          const curenrtUserValue = { ...user };
          curenrtUserValue.UserRoles = this.encryptPermissions(curenrtUserValue?.UserRoles || []);
          localStorage.setItem('authUser', JSON.stringify(curenrtUserValue));
          if (forceReload || (this.router.url != "/main/user-profile" && this.router.url != "/consumer/user-profile")) {
            location.reload();
          }

        } else {
          this.logout();
        }
      }));
  }
  RefreshAtoken(refreshToken) {
    let body = `refreshToken=${refreshToken}`;
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post(`${environment.apiUrl}/Authenticate/RefreshToken?${body}`, '', { headers });
  }

  RetrieveSession(refreshToken: string) {
    console.log(refreshToken);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'refreshToken': refreshToken,
    });
    return this.http.post<any>(`${environment.apiUrl}/Authenticate/RetrieveSession`, {}, { headers })
      .pipe(map(returnUser => {
        if (returnUser) {
          const user = { ...returnUser };
          user.Token = returnUser.Token;
          this.currentUserSubject.next(returnUser);
          const curenrtUserValue = { ...user };
          curenrtUserValue.UserRoles = this.encryptPermissions(curenrtUserValue?.UserRoles || []);
          localStorage.setItem('authUser', JSON.stringify(curenrtUserValue));
          location.reload();
        } else {
          this.logout();
        }
      }));
  }

  get isOnline$() {
    return this.networkStatus.pipe(filter(Boolean));
  }
  CheckIsVisitor(): any {
    return this.http.post(`${environment.apiUrl}/Authenticate/CheckIsVisitor`, {});
  }
  checkUsername(username: any) {
    return this.http.get(`${environment.apiUrl}/accounts/ValidateUserName?filterKey=` + username);
  }
  checkEmail(email: any, userId: any) {
    return this.http.get(`${environment.apiUrl}/accounts/ValidateEmail?filterKey=` + email + '&userId=' + userId);
  }

  hasPermission(permission: string, checkOtherRoles = false) {
    if (!this.currentUserValue) {
      return false;
    }
    const accessControls: string[] = this.currentUserValue.UserRoles;
    const accessRolesList = permission.split(',');
    const founded: string[] = [];
    accessRolesList.forEach((role) => {
      const found = accessControls.some((control) => control === role);
      if (found) {
        founded.push(role);
      }
    });

    // if (founded.length && accessControls.length) { return true; }
    if (
      founded.length &&
      accessControls.length &&
      checkOtherRoles &&
      founded.length !== accessControls.length
    ) {
      return false;
    } else if (founded.length && accessControls.length) {
      return true;
    } else {
      return false;
    }
  }

  hasPermissionPlan(permission: string, checkOtherPermissions = false) {
    if (!this.currentUserValue) {
      return false;
    }
    const accessControls: string[] = this.currentUserValue.TrainingPermissions;
    const accessPermissionsList = permission.split(',');
    const founded: string[] = [];
    accessPermissionsList.forEach((permission) => {
      const found = accessControls.some((control) => control === permission);
      if (found) {
        founded.push(permission);
      }
    });

    // if (founded.length && accessControls.length) { return true; }
    if (
      founded.length &&
      accessControls.length &&
      checkOtherPermissions &&
      founded.length !== accessControls.length
    ) {
      return false;
    } else if (founded.length && accessControls.length) {
      return true;
    } else {
      return false;
    }
  }
}
